<script setup lang="ts" generic="T extends string | number | boolean">
/*
 * Radio group built on Headless UI's RadioGroup. Pass `options` as
 * { label, value } records; v-model binds to the selected value.
 */
import { RadioGroup, RadioGroupOption } from '@headlessui/vue';

interface Option<V> {
  label: string;
  value: V;
  description?: string;
  disabled?: boolean;
}

withDefaults(
  defineProps<{
    modelValue: T;
    options: Option<T>[];
    name?: string;
    orientation?: 'vertical' | 'horizontal';
  }>(),
  { orientation: 'vertical' },
);

defineEmits<{ 'update:modelValue': [v: T] }>();
</script>

<template>
  <RadioGroup
    :model-value="modelValue"
    :class="['flex gap-2', orientation === 'horizontal' ? 'flex-row' : 'flex-col']"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <RadioGroupOption
      v-for="opt in options"
      :key="String(opt.value)"
      v-slot="{ checked }"
      :value="opt.value"
      :disabled="opt.disabled"
      as="template"
    >
      <div
        :class="[
          'flex cursor-pointer items-start gap-3 rounded-xl border px-3 py-2.5 transition-colors',
          checked
            ? 'border-primary bg-primary-50 ring-3 ring-primary/15'
            : 'border-app-border bg-app-surface hover:bg-slate-50',
          opt.disabled ? 'cursor-not-allowed opacity-50' : '',
        ]"
      >
        <span
          aria-hidden="true"
          :class="[
            'mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2',
            checked ? 'border-primary' : 'border-slate-400',
          ]"
        >
          <span v-if="checked" class="h-2 w-2 rounded-full bg-primary" />
        </span>
        <span class="flex flex-col">
          <span class="text-md font-medium text-app-fg">{{ opt.label }}</span>
          <span v-if="opt.description" class="text-xs text-app-muted">{{ opt.description }}</span>
        </span>
      </div>
    </RadioGroupOption>
  </RadioGroup>
</template>
